import { SEARCH_TAGS } from 'js/constants/App';
import { getBasePath } from 'js/utils/general';

const parseData = (item) => {
  const {
    id = '',
    title = '',
    description = '',
    thumbnail = '',
    largeImage = '',
    howToPlay1 = '',
    youWillNeed = '',
    bigIdea = '',
    gameplayTip = '',
    gameplayVisual = '',
    downloadFilename = '',
    howManyPlayers = [],
    howManyMinutes = '',
    sesameCharacter = '',
    whatDoYouWantToDo = '',
    playType = [],
    selSkillsActivityTags = '',
    curatedCollections = '',
    availableOffline = '',
    languageModification = '',
    skillTags = [],
    localisation = {},
  } = item || {};

  const minutesTag = Object.keys(SEARCH_TAGS.minutes).find((key) => {
    return noralizeString(SEARCH_TAGS.minutes[key]) === noralizeString(howManyMinutes);
  });

  const playersTags = howManyPlayers
    .map((playerCount) =>
      Object.keys(SEARCH_TAGS.players).find(
        (key) => noralizeString(SEARCH_TAGS.players[key]) === noralizeString(playerCount)
      )
    )
    .filter((tag) => tag);

  const playTypeTags = playType
    .map((type) =>
      Object.keys(SEARCH_TAGS.play_type).find(
        (key) => noralizeString(SEARCH_TAGS.play_type[key]) === noralizeString(type)
      )
    )
    .filter((tag) => tag);

  const activityTag = Object.keys(SEARCH_TAGS.activity).find((key) => {
    return noralizeString(SEARCH_TAGS.activity[key]) === noralizeString(whatDoYouWantToDo);
  });

  let finalPlayersTag = '';

  if (playersTags.length === 1) {
    finalPlayersTag =
      playersTags[0] == 'players_1'
        ? '1'
        : playersTags[0] == 'players_2'
        ? '2-4'
        : playersTags[0] == 'players_3'
        ? '5+'
        : '';
  } else if (playersTags.length === 2) {
    const tag1 = playersTags[0];
    const tag2 = playersTags[1];

    if (tag1 === 'players_1' && tag2 === 'players_2') {
      finalPlayersTag = '1-4';
    } else if (tag1 === 'players_2' && tag2 === 'players_3') {
      finalPlayersTag = '2+';
    } else if (tag1 === 'players_1' && tag2 === 'players_3') {
      finalPlayersTag = '1+';
    }
  } else if (playersTags.length === 3) {
    finalPlayersTag = '1+';
  }

  // get localisation data
  const {
    title: localizedTitle,
    description: localizedDescription,
    howToPlay1: localizedHowToPlay1,
    youWillNeed: localizedYouWillNeed,
    bigIdea: localizedBigIdea,
    gameplayTip: localizedGameplayTip,
  } = localisation || {};

  // workout the pagination count base on description, howToPlay1, gameplayTip
  const paginationCount = [description, howToPlay1, gameplayTip].filter((text) => text && text !== '').length;
  const localizedPaginationCount = [localizedDescription, localizedHowToPlay1, localizedGameplayTip].filter(
    (text) => text && text !== ''
  ).length;

  const splitSkillTags = skillTags.reduce((acc, tag) => {
    const splitTags = tag.split(',').map((t) => t.trim());
    return [...acc, ...splitTags];
  }, []);

  return {
    id,
    title,
    details: {
      downloadFilename,
      selSkillsActivityTags,
      curatedCollections,
      availableOffline,
      languageModification,
    },
    front: {
      largeImage: `${getBasePath() === '/' ? getBasePath() : getBasePath() + '/'}images/cards/${largeImage}.jpg`,
      title,
      id,
      howManyPlayers,
      skillTags: splitSkillTags,
      howManyMinutes,
      finalPlayersTag,
      localisation: {
        title: localizedTitle,
      },
    },
    back: {
      paginationCount,
      title,
      description,
      youWillNeed,
      bigIdea,
      howToPlay1,
      sesameCharacter,
      gameplayTip,
      gameplayVisual,
      localisation: {
        localizedPaginationCount,
        title: localizedTitle,
        description: localizedDescription,
        youWillNeed: localizedYouWillNeed,
        howToPlay1: localizedHowToPlay1,
        bigIdea: localizedBigIdea,
        gameplayTip: localizedGameplayTip,
      },
    },
    search: {
      minutes: minutesTag,
      players: playersTags,
      play_type: playTypeTags,
      activity: activityTag,
      avoidMaterials: youWillNeed === 'Nothing!' || localizedYouWillNeed === '¡Nada!' ? true : false,
    },
  };
};

export const parseCardsData = (data) => {
  const allIds = [];
  const byId = {};

  // search based values
  const searchIndexes = {
    minutes: {
      minutes_1: [],
      minutes_2: [],
      minutes_3: [],
    },
    players: {
      players_1: [],
      players_2: [],
      players_3: [],
    },
    play_type: {
      play_type_1: [],
      play_type_2: [],
      play_type_3: [],
      play_type_4: [],
    },
    activity: {
      activity_1: [],
      activity_2: [],
      activity_3: [],
    },
  };

  const curatedCollectionAllIds = [];
  const curatedCollectionById = {};

  if (data && Array.isArray(data)) {
    data.map((item) => {
      const { id } = item;
      allIds.push(id);

      const parsedData = parseData(item);
      byId[id] = parsedData;

      let {
        front: { skillTags },
        details: { curatedCollections },
      } = parsedData;

      curatedCollections = curatedCollections || [];
      skillTags = skillTags || [];

      [...skillTags, ...curatedCollections].forEach((skill) => {
        const trimmedSkill = skill.trim().toLowerCase();
        if (!curatedCollectionAllIds.includes(trimmedSkill)) {
          curatedCollectionAllIds.push(trimmedSkill);
        }
        if (!curatedCollectionById[trimmedSkill]) {
          curatedCollectionById[trimmedSkill] = {
            id: trimmedSkill,
            name: skill.trim(),
            cards: [],
            isCuratedCollection: curatedCollections.includes(skill),
          };
        }
        curatedCollectionById[trimmedSkill].cards.push(id);
      });

      // build search indexes
      const { search } = parsedData;
      Object.keys(searchIndexes).forEach((key) => {
        const searchValues = search[key];

        if (searchValues && Array.isArray(searchValues)) {
          searchValues.forEach((val) => {
            if (searchIndexes[key][val]) {
              searchIndexes[key][val].push(id);
            }
          });
        } else {
          if (searchIndexes[key][searchValues]) {
            searchIndexes[key][searchValues].push(id);
          }
        }
      });
    });
  }

  // ignore collections with < 3 cards
  Object.keys(curatedCollectionById).forEach((key) => {
    if (curatedCollectionById[key].cards.length < 3) {
      delete curatedCollectionById[key];
      const index = curatedCollectionAllIds.indexOf(key);
      if (index > -1) {
        curatedCollectionAllIds.splice(index, 1);
      }
    }
  });

  return {
    byId,
    allIds,
    searchIndexes,
    curatedCollections: { allIds: curatedCollectionAllIds, byId: curatedCollectionById },
  };
};

const noralizeString = (str) => {
  return (str || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/gi, '');
};
